<template>
  <div
    v-editable="blok"
    class="data-visualization"
    :style="{
      backgroundImage: `url(${backgroundImage()})`,
    }"
  >
    <CustomHeading
      v-if="blok.heading && blok.heading[0]"
      :blok="blok.heading[0]"
    />
    <ZipSearch
      v-if="blok.zip_search && blok.zip_search[0]"
      :blok="blok.zip_search[0]"
    />
    <div class="data-visualization__content">
      <Picture
        v-if="blok.image_small && blok.image_small.filename"
        v-editable="blok"
        class="data-visualization__image-small"
        :blok="makePicture(blok.image_small)"
      />
      <Statistic
        v-for="(stat, index) in blok.statistics"
        :key="stat._uid"
        :blok="stat"
        :class="
          index === 0
            ? 'data-visualization__statistic-first'
            : 'data-visualization__statistic-second'
        "
        :flipcorner="index === 0"
      />
      <Picture
        v-if="blok.image_large && blok.image_large.filename"
        v-editable="blok"
        class="data-visualization__image-large"
        :blok="makePicture(blok.image_large)"
        :height="720"
        :width="540"
      />
      <Picture
        v-if="blok.image_layered"
        v-editable="blok"
        class="data-visualization__image-layered"
        :blok="makePicture(blok.image_layered)"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DataVisualizationBlokProps, ImageBlokProps } from '@/types';

// Props
const props = defineProps<{
  blok: DataVisualizationBlokProps;
}>();

const makePicture = (src: ImageBlokProps) => ({
  image: src,
});

const backgroundImage = () => {
  const image = props.blok.image_background;

  const processedImage = imageService(image.filename, {
    focus: image.focus,
    size: 871 + 'x' + 871,
  });

  return processedImage;
};
</script>

<style lang="scss">
.data-visualization {
  @include container;

  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;

  @media all and #{$mq-m-min} {
    background-position: bottom var(--spacing-xxl) right var(--side-spacing);
    background-size: columns(14);
  }

  .custom-heading {
    @media all and #{$mq-m-min} {
      max-width: columns(18);
    }
  }

  .zip-search {
    margin-block-start: var(--spacing-m);
  }

  &__content {
    display: flex;
    flex-direction: column;
    margin-block-start: var(--spacing-m);
    position: relative;

    @media all and #{$mq-m-min} {
      margin-block-start: var(--spacing-xxl);
    }
  }

  &__image-small {
    align-self: flex-end;
    width: columns(6);

    @media all and #{$mq-m-min} {
      inset-block-start: calc(var(--spacing-xs) * -1);
      inset-inline-end: 0;
      margin-inline-end: columns(2);
      width: columns(5);
    }

    @media all and #{$mq-l-min} {
      width: columns(4);
    }
  }

  /* Small Image : Bumping specificity for position */
  & .data-visualization__image-small {
    @media all and #{$mq-m-min} {
      position: absolute;
    }
  }

  &__statistic-first {
    align-self: flex-end;
    margin-block: -10% var(--spacing-xxl);
    margin-inline-end: columns(1);
    z-index: 1;

    @media all and #{$mq-m-min} {
      inset-block-start: 35%;
      inset-inline-end: 0;
      margin-block-start: -5%;
      margin-inline-end: columns(4);
      position: absolute;
    }
  }

  &__statistic-second {
    margin-block-end: -12%;
    z-index: 1;

    @media all and #{$mq-m-min} {
      margin-block-end: -5%;
      margin-inline-start: columns(5);
    }
  }

  &__image-large {
    transform: translateX(columns(-1));
    width: columns(9);

    @media all and #{$mq-m-min} {
      transform: none;
    }
  }

  &__image-layered {
    align-self: flex-start;
    margin-block-start: -45%;
    margin-inline-start: columns(5);
    max-width: columns(10);

    @media all and #{$mq-m-min} {
      margin-block-start: -15%;
      margin-inline-start: columns(7);
      max-width: columns(6);
    }

    @media all and #{$mq-l-min} {
      margin-block-start: -20%;
    }

    &.picture--with-border-radius,
    .picture__image {
      border-start-end-radius: 7.25%; /* Using percentage here to accommodate both photo and UI image assets. */
    }
  }
}
</style>
